import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Input, Modal, Spin, Tooltip, message } from "antd";
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconArrowUpRight,
  IconCircle,
  IconCircleFilled,
  IconDownload,
  IconExternalLink,
  IconHandStop,
  IconPencil,
  IconPointer,
  IconSignature,
  IconSquare,
  IconSquareFilled,
  IconTrash,
  IconTypography,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import AnnotatorPage, {
  type DocSource,
  type TextDraft,
  type Tool,
  type ToolSettings,
} from "./AnnotatorPage";
import {
  type Annotation,
  type Point,
  newAnnotationId,
} from "@/lib/docAnnotate/annotations";
import {
  annotatedFileName,
  docKindOf,
  downloadBlob,
  exportAnnotatedImage,
  exportAnnotatedPdf,
} from "@/lib/docAnnotate/exporter";
import { loadPdf } from "@/lib/docAnnotate/pdfjs";
import { orpcClient } from "@/lib/orpc-client";
import { getUnlockedSignature, setUnlockedSignature } from "@/lib/signature/cache";
import { sessionEmployeeId } from "@/lib/signature/session";

export interface AnnotatableFile {
  name: string;
  url: string;
}

interface DocumentAnnotatorProps {
  file: AnnotatableFile | null;
  onClose: () => void;
}

type PageSize = { width: number; height: number };

const COLORS = ["#111111", "#e53935", "#1e5bd8", "#2e7d32", "#f9a825"];
const WIDTHS = [2, 4, 8];
const TEXT_SIZES: Record<number, number> = { 2: 14, 4: 20, 8: 32 };
const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
const SIGNATURE_WIDTH_RATIO = 0.25;
const HISTORY_LIMIT = 100;

const TOOLS: { tool: Tool; icon: React.ReactNode; key: string }[] = [
  { tool: "pan", icon: <IconHandStop size={18} />, key: "pan" },
  { tool: "select", icon: <IconPointer size={18} />, key: "select" },
  { tool: "pen", icon: <IconPencil size={18} />, key: "pen" },
  { tool: "text", icon: <IconTypography size={18} />, key: "text" },
  { tool: "rect", icon: <IconSquare size={18} />, key: "rect" },
  { tool: "rectFilled", icon: <IconSquareFilled size={18} />, key: "rectFilled" },
  { tool: "ellipse", icon: <IconCircle size={18} />, key: "ellipse" },
  { tool: "ellipseFilled", icon: <IconCircleFilled size={18} />, key: "ellipseFilled" },
  { tool: "arrow", icon: <IconArrowUpRight size={18} />, key: "arrow" },
];

function signatureCode(err: unknown) {
  return (err as { code?: string } | null)?.code;
}

function DocumentAnnotator({ file, onClose }: DocumentAnnotatorProps) {
  const { t } = useTranslation();
  const kind = docKindOf(file?.name ?? file?.url);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bytesRef = useRef<Uint8Array | null>(null);
  const leavingRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [source, setSource] = useState<DocSource | null>(null);
  const [pages, setPages] = useState<PageSize[]>([]);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoom, setZoom] = useState(1);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [past, setPast] = useState<Annotation[][]>([]);
  const [future, setFuture] = useState<Annotation[][]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [settings, setSettings] = useState<ToolSettings>({
    tool: "pan",
    color: COLORS[0],
    width: WIDTHS[1],
    textSize: TEXT_SIZES[WIDTHS[1]],
  });
  const [textDraft, setTextDraft] = useState<TextDraft | null>(null);
  const [downloading, setDownloading] = useState(false);

  const [signature, setSignature] = useState<string | null>(null);
  const [signatureAspect, setSignatureAspect] = useState(0.4);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState<{ page: number; point: Point } | null>(null);
  const [dragGhost, setDragGhost] = useState<Point | null>(null);

  const open = !!file;

  useEffect(() => {
    if (!file) return;
    let active = true;
    setLoading(true);
    setLoadError(false);
    setSource(null);
    setPages([]);
    setAnnotations([]);
    setPast([]);
    setFuture([]);
    setSelectedId(null);
    setTextDraft(null);
    setZoom(1);
    bytesRef.current = null;
    leavingRef.current = false;
    (async () => {
      try {
        if (kind === "pdf") {
          const res = await fetch(file.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const bytes = new Uint8Array(await res.arrayBuffer());
          const pdf = await loadPdf(bytes);
          const sizes: PageSize[] = [];
          for (let i = 1; i <= pdf.numPages; i++) {
            const vp = (await pdf.getPage(i)).getViewport({ scale: 1 });
            sizes.push({ width: vp.width, height: vp.height });
          }
          if (!active) return;
          bytesRef.current = bytes;
          setSource({ kind: "pdf", pdf });
          setPages(sizes);
        } else {
          const res = await fetch(file.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const objectUrl = URL.createObjectURL(await res.blob());
          const img = new Image();
          img.src = objectUrl;
          await img.decode();
          if (!active) return;
          setSource({ kind: "image", img });
          setPages([{ width: img.naturalWidth, height: img.naturalHeight }]);
        }
      } catch (err) {
        console.error("documentAnnotator.load_ERROR", err);
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [file, kind]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    sessionEmployeeId().then((id) => {
      const cached = id ? getUnlockedSignature(id) : undefined;
      if (active && cached) setSignature(cached);
    });
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!signature) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth) setSignatureAspect(img.naturalHeight / img.naturalWidth);
    };
    img.src = signature;
  }, [signature]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, source]);

  const commit = useCallback(
    (next: Annotation[]) => {
      setPast((p) => [...p.slice(-HISTORY_LIMIT + 1), annotations]);
      setAnnotations(next);
      setFuture([]);
    },
    [annotations],
  );

  const addAnnotation = (a: Annotation) => commit([...annotations, a]);

  const changeAnnotation = (a: Annotation) =>
    commit(annotations.map((x) => (x.id === a.id ? a : x)));

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    commit(annotations.filter((a) => a.id !== selectedId));
    setSelectedId(null);
  }, [annotations, commit, selectedId]);

  const undo = useCallback(() => {
    if (!past.length) return;
    setFuture((f) => [annotations, ...f]);
    setAnnotations(past[past.length - 1]);
    setPast((p) => p.slice(0, -1));
    setSelectedId(null);
  }, [annotations, past]);

  const redo = useCallback(() => {
    if (!future.length) return;
    setPast((p) => [...p, annotations]);
    setAnnotations(future[0]);
    setFuture((f) => f.slice(1));
    setSelectedId(null);
  }, [annotations, future]);

  useEffect(() => {
    if (!annotations.length) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (leavingRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [annotations.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        deleteSelected();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, selectedId, deleteSelected, undo, redo]);

  const setTool = (tool: Tool) => {
    setSettings((s) => ({ ...s, tool }));
    if (tool !== "select") setSelectedId(null);
  };

  const handleTextRequest = (page: number, point: Point) => {
    if (textDraft) {
      commitText();
      return;
    }
    setTextDraft({
      page,
      x: point.x,
      y: point.y,
      value: "",
      size: settings.textSize,
      color: settings.color,
    });
  };

  const commitText = () => {
    const draft = textDraft;
    setTextDraft(null);
    if (!draft || !draft.value.trim()) return;
    addAnnotation({
      id: newAnnotationId(),
      page: draft.page,
      type: "text",
      color: draft.color,
      x: draft.x,
      y: draft.y,
      text: draft.value.replace(/\s+$/, ""),
      size: draft.size,
    });
  };

  const placeSignature = (image: string, page: number, center: Point) => {
    const size = pages[page];
    if (!size) return;
    const w = Math.min(size.width * SIGNATURE_WIDTH_RATIO, 200);
    const h = w * signatureAspect;
    const x = Math.min(Math.max(0, center.x - w / 2), size.width - w);
    const y = Math.min(Math.max(0, center.y - h / 2), size.height - h);
    const id = newAnnotationId();
    addAnnotation({ id, page, type: "signature", color: "#000", x, y, w, h, image });
    setSettings((s) => ({ ...s, tool: "select" }));
    setSelectedId(id);
  };

  const requestSignature = (placement: { page: number; point: Point } | null) => {
    if (signature) {
      if (placement) placeSignature(signature, placement.page, placement.point);
      return;
    }
    setPendingPlacement(placement);
    setUnlockPassword("");
    setUnlockOpen(true);
  };

  const handleUnlock = async () => {
    if (!unlockPassword) return;
    setUnlocking(true);
    try {
      const result = await orpcClient.employeeSignature.read({ password: unlockPassword });
      const id = await sessionEmployeeId();
      if (id) setUnlockedSignature(id, result.image);
      setSignature(result.image);
      setUnlockOpen(false);
      setUnlockPassword("");
      message.success(t("docAnnotate.signatureReady"));
      if (pendingPlacement) placeSignature(result.image, pendingPlacement.page, pendingPlacement.point);
      else setTool("signature");
      setPendingPlacement(null);
    } catch (err) {
      const code = signatureCode(err);
      if (code === "SIGNATURE_NOT_UPLOADED") {
        setUnlockOpen(false);
        setPendingPlacement(null);
        Modal.warning({
          title: t("docAnnotate.signatureMissingTitle"),
          content: t("docAnnotate.signatureMissing"),
          okText: t("docAnnotate.ok"),
        });
      } else if (code === "SIGNATURE_PASSWORD_INCORRECT") {
        message.error(t("employee.signature.passwordIncorrect"));
      } else if (code === "FORBIDDEN") {
        message.error(t("employee.signature.forbidden"));
      } else {
        message.error(t("employee.signature.requestFailed"));
      }
    } finally {
      setUnlocking(false);
    }
  };

  const startSignatureDrag = (e: React.PointerEvent) => {
    if (!signature) return;
    e.preventDefault();
    setDragGhost({ x: e.clientX, y: e.clientY });
    const image = signature;
    const move = (ev: PointerEvent) => setDragGhost({ x: ev.clientX, y: ev.clientY });
    const up = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      setDragGhost(null);
      const target = document
        .elementsFromPoint(ev.clientX, ev.clientY)
        .map((el) => (el as HTMLElement).closest?.("[data-page-index]") as HTMLElement | null)
        .find(Boolean);
      if (!target) return;
      const page = Number(target.dataset.pageIndex);
      const rect = target.getBoundingClientRect();
      const size = pages[page];
      if (!size) return;
      placeSignature(image, page, {
        x: ((ev.clientX - rect.left) / rect.width) * size.width,
        y: ((ev.clientY - rect.top) / rect.height) * size.height,
      });
    };
    const cancel = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      setDragGhost(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  };

  const handleDownload = async () => {
    if (!file || !source) return;
    setDownloading(true);
    try {
      const suffix = t("docAnnotate.annotatedSuffix");
      if (source.kind === "pdf" && bytesRef.current) {
        const out = await exportAnnotatedPdf(bytesRef.current, annotations);
        downloadBlob(new Blob([new Uint8Array(out)], { type: "application/pdf" }), annotatedFileName(file.name, suffix, "pdf"));
      } else if (source.kind === "image") {
        const blob = await exportAnnotatedImage(source.img, annotations);
        downloadBlob(blob, annotatedFileName(file.name, suffix, "png"));
      }
      message.success(t("docAnnotate.downloaded"));
    } catch (err) {
      console.error("documentAnnotator.download_ERROR", err);
      message.error(t("docAnnotate.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    if (!annotations.length) {
      onClose();
      return;
    }
    Modal.confirm({
      title: t("docAnnotate.closeConfirmTitle"),
      content: t("docAnnotate.closeConfirm"),
      okText: t("docAnnotate.closeAnyway"),
      cancelText: t("docAnnotate.keepEditing"),
      okButtonProps: { danger: true },
      onOk: () => {
        leavingRef.current = true;
        onClose();
      },
    });
  };

  // const setWidth = (width: number) =>
  //   setSettings((s) => ({ ...s, width, textSize: TEXT_SIZES[width] }));

  const fitWidth = Math.max(200, containerWidth - 32);
  const maxPageWidth = Math.max(1, ...pages.map((p) => p.width));
  const scale = (fitWidth / maxPageWidth) * zoom;
  const zoomIndex = ZOOMS.indexOf(zoom);

  if (!open) return null;

  const toolButton = (tool: Tool, icon: React.ReactNode, key: string) => (
    <Tooltip key={tool} title={t(`docAnnotate.tool.${key}`)}>
      <Button
        aria-label={t(`docAnnotate.tool.${key}`)}
        aria-pressed={settings.tool === tool}
        type={settings.tool === tool ? "primary" : "default"}
        icon={icon}
        onClick={() => setTool(tool)}
        style={{ minWidth: 40, height: 40 }}
      />
    </Tooltip>
  );

  return createPortal(
    <div
      role="dialog"
      aria-label={file?.name}
      data-testid="document-annotator"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        background: "#525659",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: "8px 12px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ fontWeight: 600, color: "#444", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={file?.name}>
          {file?.name}
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {TOOLS.map(({ tool, icon, key }) => toolButton(tool, icon, key))}
          <Tooltip title={t("docAnnotate.tool.signature")}>
            <Button
              aria-label={t("docAnnotate.tool.signature")}
              aria-pressed={settings.tool === "signature"}
              type={settings.tool === "signature" ? "primary" : "default"}
              icon={<IconSignature size={18} />}
              onClick={() => {
                if (signature) setTool("signature");
                else requestSignature(null);
              }}
              style={{ minWidth: 40, height: 40 }}
            >
              {t("docAnnotate.tool.signature")}
            </Button>
          </Tooltip>
        </div>
        {/*
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={t("docAnnotate.color", { color: c })}
              aria-pressed={settings.color === c}
              onClick={() => setSettings((s) => ({ ...s, color: c }))}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: c,
                border: settings.color === c ? "3px solid #1677ff" : "2px solid #fff",
                boxShadow: "0 0 0 1px #ccc",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
        */}
        {/*
        <div style={{ display: "flex", gap: 4 }}>
          {WIDTHS.map((w) => (
            <Button
              key={w}
              aria-label={t("docAnnotate.width", { width: w })}
              aria-pressed={settings.width === w}
              type={settings.width === w ? "primary" : "default"}
              onClick={() => setWidth(w)}
              style={{ width: 40, height: 40, padding: 0 }}
            >
              <span style={{ display: "inline-block", width: 18, height: w, background: "currentColor", borderRadius: w }} />
            </Button>
          ))}
        </div>
        */}
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title={t("docAnnotate.undo")}>
            <Button aria-label={t("docAnnotate.undo")} icon={<IconArrowBackUp size={18} />} disabled={!past.length} onClick={undo} style={{ height: 40 }} />
          </Tooltip>
          <Tooltip title={t("docAnnotate.redo")}>
            <Button aria-label={t("docAnnotate.redo")} icon={<IconArrowForwardUp size={18} />} disabled={!future.length} onClick={redo} style={{ height: 40 }} />
          </Tooltip>
          <Tooltip title={t("docAnnotate.delete")}>
            <Button aria-label={t("docAnnotate.delete")} icon={<IconTrash size={18} />} disabled={!selectedId} onClick={deleteSelected} style={{ height: 40 }} />
          </Tooltip>
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <Button aria-label={t("docAnnotate.zoomOut")} icon={<IconZoomOut size={18} />} disabled={zoomIndex <= 0} onClick={() => setZoom(ZOOMS[zoomIndex - 1])} style={{ height: 40 }} />
          <span style={{ minWidth: 44, textAlign: "center", fontSize: 13, color: "#555" }}>{Math.round(zoom * 100)}%</span>
          <Button aria-label={t("docAnnotate.zoomIn")} icon={<IconZoomIn size={18} />} disabled={zoomIndex >= ZOOMS.length - 1} onClick={() => setZoom(ZOOMS[zoomIndex + 1])} style={{ height: 40 }} />
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Button
            type="primary"
            icon={<IconDownload size={18} />}
            loading={downloading}
            disabled={!source}
            onClick={handleDownload}
            style={{ height: 40 }}
          >
            {t("docAnnotate.download")}
          </Button>
          <Button aria-label={t("docAnnotate.close")} icon={<IconX size={18} />} onClick={handleClose} style={{ height: 40 }} />
        </div>
      </div>

      {signature && (
        <div
          style={{
            background: "#fffbe6",
            borderBottom: "1px solid #ffe58f",
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 13,
            color: "#8c6d1f",
          }}
        >
          <div
            data-testid="annotator-signature-chip"
            onPointerDown={startSignatureDrag}
            style={{
              background: "#fff",
              border: "1px dashed #d4b106",
              borderRadius: 6,
              padding: 4,
              cursor: "grab",
              touchAction: "none",
              userSelect: "none",
            }}
          >
            <img src={signature} alt={t("docAnnotate.tool.signature")} draggable={false} style={{ height: 36, display: "block" }} />
          </div>
          <span>{t("docAnnotate.signatureDragHint")}</span>
        </div>
      )}

      <div ref={scrollRef} data-testid="annotator-scroll" style={{ flex: 1, overflow: "auto", padding: 16 }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
            <Spin size="large" />
          </div>
        )}
        {loadError && (
          <div style={{ maxWidth: 420, margin: "80px auto", background: "#fff", borderRadius: 8, padding: 24, textAlign: "center" }}>
            <p style={{ marginBottom: 16 }}>{t("docAnnotate.loadFailed")}</p>
            <Button icon={<IconExternalLink size={16} />} onClick={() => file && window.open(file.url, "_blank")}>
              {t("docAnnotate.openInNewTab")}
            </Button>
          </div>
        )}
        {source &&
          containerWidth > 0 &&
          pages.map((size, index) => (
            <AnnotatorPage
              key={index}
              index={index}
              source={source}
              pageWidth={size.width}
              pageHeight={size.height}
              cssWidth={size.width * scale}
              annotations={annotations.filter((a) => a.page === index)}
              selectedId={selectedId}
              settings={settings}
              onAdd={addAnnotation}
              onChange={changeAnnotation}
              onSelect={setSelectedId}
              onTextRequest={handleTextRequest}
              onSignatureRequest={(page, point) => requestSignature({ page, point })}
              textDraft={textDraft?.page === index ? textDraft : null}
              textPlaceholder={t("docAnnotate.textPlaceholder")}
              onTextDraftChange={(value) => setTextDraft((d) => (d ? { ...d, value } : d))}
              onTextCommit={commitText}
              onTextCancel={() => setTextDraft(null)}
            />
          ))}
      </div>

      {dragGhost && signature && (
        <img
          src={signature}
          alt=""
          style={{
            position: "fixed",
            left: dragGhost.x,
            top: dragGhost.y,
            height: 48,
            transform: "translate(-50%, -50%)",
            opacity: 0.8,
            pointerEvents: "none",
            zIndex: 1001,
          }}
        />
      )}

      <Modal
        open={unlockOpen}
        title={t("docAnnotate.unlockTitle")}
        onCancel={() => {
          setUnlockOpen(false);
          setPendingPlacement(null);
        }}
        onOk={handleUnlock}
        okText={t("employee.signature.confirm")}
        cancelText={t("employee.edit.cancel")}
        okButtonProps={{ disabled: !unlockPassword, loading: unlocking }}
        destroyOnClose
      >
        <p style={{ fontSize: 13, color: "#888" }}>{t("docAnnotate.unlockHint")}</p>
        <Input.Password
          autoComplete="new-password"
          name="signature-pin"
          autoFocus
          aria-label={t("employee.signature.password")}
          placeholder={t("employee.signature.password")}
          value={unlockPassword}
          maxLength={128}
          onChange={(e) => setUnlockPassword(e.target.value)}
          onPressEnter={handleUnlock}
        />
      </Modal>
    </div>,
    document.body,
  );
}

export default DocumentAnnotator;
